import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { CreditCard, Minus, Plus, RefreshCw, TrendingUp, Package, Monitor } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * QuantityStepper — accessible numeric input with +/- buttons.
 * Enforces min/max at the UI layer; server always re-validates.
 */
function QuantityStepper({ label, value, onChange, min = 0, max = 20, testIdPrefix, icon: Icon }) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="w-4 h-4 text-gray-600 flex-shrink-0" />}
        <Label className="text-sm text-gray-700 truncate">{label}</Label>
      </div>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={dec}
          disabled={value <= min}
          data-testid={`${testIdPrefix}-dec`}
        >
          <Minus className="w-3.5 h-3.5" />
        </Button>
        <span
          className="w-10 text-center font-semibold text-gray-900 tabular-nums"
          data-testid={`${testIdPrefix}-value`}
        >
          {value}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={inc}
          disabled={value >= max}
          data-testid={`${testIdPrefix}-inc`}
        >
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

const formatINR = (paise) => {
  if (typeof paise !== "number") return "—";
  return "₹" + (paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Renew / Upgrade Plan dialog.
 * Customer picks billing cycle + platform quantities. Preview endpoint is
 * called on every change; server is the sole source of truth for the amount.
 */
export function RenewPlanDialog({ open, onOpenChange, product, license, authHeaders }) {
  const initialCycle = product?.billing_cycle || "yearly";
  const initialMt5 = license?.mt5_max_activations || 2;
  const initialNt = license?.nt_max_activations || 0;
  const [cycle, setCycle] = useState(initialCycle);
  const [mt5, setMt5] = useState(initialMt5);
  const [nt, setNt] = useState(initialNt);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset state whenever dialog opens with different product/license.
  useEffect(() => {
    if (open) {
      setCycle(product?.billing_cycle || "yearly");
      setMt5(license?.mt5_max_activations || 2);
      setNt(license?.nt_max_activations || 0);
    }
  }, [open, product?.product_id, license?.id]);

  // Debounced preview call.
  useEffect(() => {
    if (!open) return;
    if (mt5 + nt < 2) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${API}/client/renew-preview`,
          {
            product_id: product?.product_id,
            billing_cycle: cycle,
            mt5_quantity: mt5,
            ninjatrader_quantity: nt,
          },
          { headers: authHeaders() }
        );
        if (!cancelled) setPreview(res.data);
      } catch (err) {
        if (!cancelled) {
          setPreview(null);
          const detail = err.response?.data?.detail;
          if (typeof detail === "string") toast.error(detail);
        }
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, cycle, mt5, nt, product?.product_id, authHeaders]);

  const total = mt5 + nt;
  const canSubmit = total >= 2 && !!preview && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/client/renew`,
        {
          product_id: product?.product_id,
          billing_cycle: cycle,
          mt5_quantity: mt5,
          ninjatrader_quantity: nt,
        },
        { headers: authHeaders() }
      );
      const url = res.data?.short_url;
      if (!url) throw new Error("No checkout URL");
      window.location.href = url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't start renewal. Try again.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="renew-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-indigo-600" />
            Renew / Upgrade Plan
          </DialogTitle>
          <DialogDescription>
            Choose your billing cycle and platform quantities. The new plan takes effect after payment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">
              Billing Cycle
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {["quarterly", "yearly"].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCycle(c)}
                  className={`py-2 px-3 rounded-md border text-sm font-medium transition ${
                    cycle === c
                      ? "border-indigo-600 bg-indigo-50 text-indigo-900"
                      : "border-gray-200 text-gray-700 hover:border-gray-300"
                  }`}
                  data-testid={`cycle-${c}`}
                >
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-2">
            <QuantityStepper
              label="MT5 Accounts"
              value={mt5}
              onChange={setMt5}
              min={0}
              max={20}
              testIdPrefix="renew-mt5"
              icon={Monitor}
            />
            <QuantityStepper
              label="NinjaTrader Devices"
              value={nt}
              onChange={setNt}
              min={0}
              max={20}
              testIdPrefix="renew-nt"
              icon={Package}
            />
          </div>

          {total < 2 && (
            <p className="text-xs text-red-600" data-testid="renew-min-warning">
              Minimum 2 total activations required. Add {2 - total} more.
            </p>
          )}

          <div className="bg-indigo-50 border border-indigo-100 rounded-md p-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total activations</span>
              <span className="font-semibold" data-testid="renew-total">{total}</span>
            </div>
            <div className="flex justify-between text-sm items-baseline">
              <span className="text-gray-600">Price</span>
              <span className="font-bold text-lg text-indigo-900 tabular-nums" data-testid="renew-price">
                {previewing ? (
                  <RefreshCw className="w-4 h-4 animate-spin inline" />
                ) : (
                  formatINR(preview?.calculated_price_paise)
                )}
                <span className="text-xs font-normal text-gray-500 ml-1">
                  / {cycle}
                </span>
              </span>
            </div>
            {preview?.pricing_model === "tiered" && (
              <p className="text-xs text-gray-500 mt-1">
                {formatINR(preview.per_seat_paise)} per activation × {total}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="renew-submit-btn"
          >
            {submitting ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Opening…
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Continue to Payment
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Add More Accounts (mid-cycle seat upgrade) dialog.
 * Customer picks additional MT5 / NT deltas. Prorated price is fetched from
 * the preview endpoint; server computes the final Razorpay amount.
 */
export function AddSeatsDialog({ open, onOpenChange, product, license, authHeaders }) {
  const [addMt5, setAddMt5] = useState(0);
  const [addNt, setAddNt] = useState(0);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAddMt5(0);
      setAddNt(0);
      setPreview(null);
    }
  }, [open, product?.product_id, license?.id]);

  useEffect(() => {
    if (!open) return;
    if (addMt5 + addNt < 1) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${API}/client/upgrade-seats-preview`,
          {
            product_id: product?.product_id,
            additional_mt5_quantity: addMt5,
            additional_ninjatrader_quantity: addNt,
          },
          { headers: authHeaders() }
        );
        if (!cancelled) setPreview(res.data);
      } catch (err) {
        if (!cancelled) {
          setPreview(null);
          const detail = err.response?.data?.detail;
          if (typeof detail === "string") toast.error(detail);
        }
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, addMt5, addNt, product?.product_id, authHeaders]);

  const addTotal = addMt5 + addNt;
  const canSubmit = addTotal >= 1 && !!preview && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/client/upgrade-seats`,
        {
          product_id: product?.product_id,
          additional_mt5_quantity: addMt5,
          additional_ninjatrader_quantity: addNt,
        },
        { headers: authHeaders() }
      );
      const url = res.data?.short_url;
      if (!url) throw new Error("No checkout URL");
      window.location.href = url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't start upgrade. Try again.");
      setSubmitting(false);
    }
  };

  const currentMt5 = license?.mt5_max_activations ?? 2;
  const currentNt = license?.nt_max_activations ?? 0;
  const currentTotal = license?.total_purchased_activations ?? currentMt5 + currentNt;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-seats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Add More Accounts
          </DialogTitle>
          <DialogDescription>
            Add extra seats to your current subscription. You&apos;ll be charged only for the remaining days of this billing period. Your expiry date won&apos;t change.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Current</p>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <p className="text-xs text-gray-500">MT5</p>
                <p className="font-semibold" data-testid="current-mt5">{currentMt5}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">NinjaTrader</p>
                <p className="font-semibold" data-testid="current-nt">{currentNt}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total</p>
                <p className="font-semibold" data-testid="current-total">{currentTotal}</p>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Add</Label>
            <QuantityStepper
              label="Additional MT5"
              value={addMt5}
              onChange={setAddMt5}
              min={0}
              max={20}
              testIdPrefix="add-mt5"
              icon={Monitor}
            />
            <QuantityStepper
              label="Additional NinjaTrader"
              value={addNt}
              onChange={setAddNt}
              min={0}
              max={20}
              testIdPrefix="add-nt"
              icon={Package}
            />
          </div>

          {addTotal >= 1 && preview && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-md p-3 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Additional seats</span>
                <span className="font-semibold" data-testid="add-total">{addTotal}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Remaining period</span>
                <span className="font-mono text-xs">
                  {preview.proration?.remaining_days}/{preview.proration?.billing_period_days} days
                </span>
              </div>
              <div className="flex justify-between items-baseline pt-1.5 border-t border-emerald-100">
                <span className="text-gray-700 font-medium">Prorated amount</span>
                <span
                  className="font-bold text-lg text-emerald-900 tabular-nums"
                  data-testid="add-price"
                >
                  {previewing ? <RefreshCw className="w-4 h-4 animate-spin inline" /> : formatINR(preview.calculated_price_paise)}
                </span>
              </div>
              <p className="text-xs text-gray-500 pt-1">
                After payment: MT5 {preview.new_quantities_after_upgrade?.mt5}, NT {preview.new_quantities_after_upgrade?.ninjatrader}, Total {preview.new_quantities_after_upgrade?.total}
              </p>
            </div>
          )}

          {addTotal < 1 && (
            <p className="text-xs text-gray-500 text-center py-1">
              Choose at least 1 additional seat to see the prorated price.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="add-seats-submit-btn"
          >
            {submitting ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Opening…
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Continue to Payment
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
